import { differenceInDays } from "date-fns";
import { useState, useMemo, lazy, Suspense } from "react";
import { ActionButtons } from "./ActionButtons";
const PaymentHistoryDialog = lazy(() =>
  import("./PaymentHistoryDialog").then((module) => ({
    default: module.PaymentHistoryDialog,
  })),
);
import { AddTopUpModal } from "./payment-history/AddTopUpModal";
import { PaymentInformation } from "./PaymentInformation";
import { QuickPaymentAction } from "./QuickPaymentAction";
import { SentimentIndicator } from "./SentimentIndicator";
import { SubscriptionAmount } from "./SubscriptionAmount";
import { SubscriptionHeader } from "./SubscriptionHeader";
import { useUserSettingsContext } from "@/contexts/UserSettingsContext";
import { useCategories } from "@/hooks/useCategories";
import { useDerivedNextPayment } from "@/hooks/useDerivedNextPayment";
import { usePaymentHistory } from "@/hooks/usePaymentHistory";
import { useQuickPayment } from "@/hooks/useQuickPayment";
import { useSubscriptions } from "@/hooks/useSubscriptions";
import { offlineMutate } from "@/lib/offlineToast";
import { useSubscriptionTags } from "@/hooks/useSubscriptionTags";
import { calculatePeriodStart } from "@/lib/subscriptionDateUtils";
import { formatLastPayment } from "@/lib/utils";
import { usePerformanceMeasure } from "@/perf/usePerformanceMeasure";
import { type Subscription } from "@/types/subscription";

interface SubscriptionCardProps {
  subscription: Subscription;
  onEdit?: () => void;
  currentPeriod?: { month: number; year: number };
}

export const SubscriptionCard = ({
  subscription,
  onEdit,
  currentPeriod,
}: SubscriptionCardProps) => {
  // Measure render time for SubscriptionCard
  usePerformanceMeasure("SubscriptionCard");

  const { deleteSubscription, updateSubscription } = useSubscriptions();
  const { getPaymentsBySubscription } = usePaymentHistory();
  const { createQuickPayment } = useQuickPayment();
  const { categories: dbCategories } = useCategories();
  const { tagsBySubscriptionId } = useSubscriptionTags();
  const { hasYnabToken } = useUserSettingsContext();
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [showLastPayment, setShowLastPayment] = useState(false);
  const [addPaymentModalOpen, setAddPaymentModalOpen] = useState(false);

  // Simplified calculations using startDate
  const repoPayments = getPaymentsBySubscription(subscription.id);

  // Derive next payment from history/seed to avoid stale explicit nextPayment
  // Hook must always be called; but PAYG subscriptions do not use the derived next date.
  const derivedNextFromHook = useDerivedNextPayment(subscription, repoPayments);
  const derivedNext =
    subscription.billingType === "pay_as_you_go" ? null : derivedNextFromHook;
  const daysUntil = derivedNext
    ? differenceInDays(derivedNext, new Date())
    : Infinity;
  const isOverdue = derivedNext ? daysUntil < 0 : false;
  const isUrgent = derivedNext ? daysUntil <= 3 && daysUntil > 0 : false;

  // Calculate period start using centralized logic
  const periodStart = useMemo(() => {
    if (!derivedNext || subscription.billingType === "pay_as_you_go") {
      return new Date(0); // epoch fallback for PAYG
    }
    if (!subscription.frequency) {
      return new Date(0);
    }
    return calculatePeriodStart(
      derivedNext,
      subscription.frequency as any,
      subscription.customFrequencyDays
    );
  }, [derivedNext, subscription.frequency, subscription.customFrequencyDays, subscription.billingType]);

  // Check if there are any paid payments in the current period
  // Payments may come from different shapes (tests use `date`, repos use `payment_date`).
  const getPaymentDate = (payment: any) => {
    if (payment?.date) return new Date(payment.date);
    if (payment?.payment_date) return new Date(payment.payment_date);
    return new Date(0); // use epoch as safe fallback to avoid treating unknown shapes as "now"
  };
  // Prefer the canonical subscription.paymentHistory when available (tests set this),
  // otherwise fall back to repoPayments returned by the payment hook.
  const paymentsToCheck =
    subscription.paymentHistory && subscription.paymentHistory.length > 0
      ? subscription.paymentHistory
      : (repoPayments as any[]);
  const hasPaidPaymentInPeriod =
    !isOverdue &&
    (paymentsToCheck as any[]).some(
      (payment) =>
        payment.status === "paid" &&
        getPaymentDate(payment) >= periodStart &&
        getPaymentDate(payment) < derivedNext,
    );

  const shouldShowMarkAsPaidFlag =
    subscription.status === "active" &&
    subscription.reconciliationStatus === "pending" &&
    !hasPaidPaymentInPeriod;

  // Direct formatting functions
  const formatAmount = () => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: subscription.currency,
    }).format(subscription.amount);
  };

  const getFrequencyText = () => {
    switch (subscription.frequency) {
      case "monthly":
        return "/month";
      case "yearly":
        return "/year";
      case "weekly":
        return "/week";
      case "custom":
        return `/${subscription.customFrequencyDays} days`;
      default:
        return "";
    }
  };

  // Tag objects for display
  const tagObjects = useMemo(() => {
    const ids = tagsBySubscriptionId[subscription.id] || [];
    return ids
      .map((id) => dbCategories.find((c) => c.id === id))
      .filter(Boolean) as {
      id: string;
      name: string;
      color?: string;
    }[];
  }, [tagsBySubscriptionId, subscription.id, dbCategories]);

  // Event handlers
  const handleMarkAsPaid = async () => {
    try {
      // PAYG should open AddTopUpModal so user can enter amount/date instead of quick-creating
      if (subscription.billingType === "pay_as_you_go") {
        setAddPaymentModalOpen(true);
        return;
      }

      // Create payment - NextPaymentService will automatically update next_payment_date
      await createQuickPayment({
        subscription_id: subscription.id,
        amount: subscription.amount,
        currency: subscription.currency,
        notes: "Quick payment",
      });

      // Query invalidation in usePaymentHistory will refresh subscription data
    } catch (error) {
      console.error("Failed to mark as paid:", error);
    }
  };

  const handleDelete = async () => {
    try {
      await offlineMutate(deleteSubscription, subscription.id);
    } catch (error) {
      console.error("Failed to delete subscription:", error);
    }
  };

  const handleShowPaymentHistory = () => {
    setShowPaymentHistory(true);
  };

  return (
    <div className="subscription-card group relative p-6">
      {/* Quick Action Overlay */}
      <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-r from-success/20 to-transparent opacity-0 transition-all duration-300 group-hover:opacity-100" />

      <div className="relative z-10">
        <SubscriptionHeader
          subscription={subscription}
          tagObjects={tagObjects}
        />

        <SubscriptionAmount subscription={subscription} />

        <PaymentInformation
          subscription={subscription}
          showLastPayment={showLastPayment}
          setShowLastPayment={setShowLastPayment}
          derivedNext={derivedNext}
          daysUntil={daysUntil}
          isOverdue={isOverdue}
          isUrgent={isUrgent}
          formatLastPayment={formatLastPayment}
        />

        {/* Notes */}
        {subscription.notes && (
          <p className="mb-4 text-xs italic text-muted-foreground">
            {subscription.notes}
          </p>
        )}

        {/* Choose handler explicitly so PAYG opens modal without invoking quick-create */}
        <QuickPaymentAction
          subscription={subscription}
          shouldShow={shouldShowMarkAsPaidFlag}
          onMarkAsPaid={
            subscription.billingType === "pay_as_you_go"
              ? () => setAddPaymentModalOpen(true)
              : handleMarkAsPaid
          }
          formatAmount={formatAmount}
          getFrequencyText={getFrequencyText}
        />

        <ActionButtons
          subscription={subscription}
          onEdit={onEdit}
          onDelete={handleDelete}
          onShowPaymentHistory={handleShowPaymentHistory}
          paymentHistoryCount={repoPayments.length}
          currentPeriod={currentPeriod}
        />

        <SentimentIndicator sentiment={subscription.sentiment} />
      </div>

      <Suspense fallback={<div />}>
        <PaymentHistoryDialog
          subscription={subscription}
          open={showPaymentHistory}
          onOpenChange={setShowPaymentHistory}
          ynabAvailable={hasYnabToken}
        />
      </Suspense>

      <AddTopUpModal
        open={addPaymentModalOpen}
        onOpenChange={setAddPaymentModalOpen}
        subscription={subscription}
      />
    </div>
  );
};
